import { useEffect } from 'react'
import { useExistingIisServers } from '@/hooks/useInfra'
import { lookupErrorMessage } from '@/services/infraLive'
import { useInfrastructureWizard, type IisAddOnKey } from '@/stores/infrastructureWizard'
import type { IisServer } from '@/types/infrastructure'
import styles from './Infrastructure.module.css'
import { LookupSelect } from './LookupSelect'
import { ReadOnlyInfoCard } from './ReadOnlyInfoCard'
import { YesNoQuestion } from './YesNoQuestion'

interface IisAddOnCardProps {
  addOnKey: IisAddOnKey
  /** The question, for example "Does the customer use Customer Portal?". */
  question: string
  /** The name used in labels. */
  name: string
}

export const NO_IIS_SERVER_MESSAGE = 'No existing IIS server found in this environment'

const details = (server: IisServer) => [
  { label: 'Name', value: server.name, mono: true },
  { label: 'Resource group', value: server.resourceGroup, mono: true },
  { label: 'Image', value: server.image },
  { label: 'VNet / subnet', value: `${server.vnet} / ${server.subnet}`, mono: true },
  { label: 'IP address', value: server.ip, mono: true },
]

/**
 * Customer Portal or one of the product's components (WMS, Trav API Portal, …): whether the customer uses it, and the existing
 * IIS server it uses. The server is never created or edited. One server is shown and preselected, several are offered to choose
 * from, none is an empty state that blocks "Next". Answering No hides the card and clears the choice.
 */
export function IisAddOnCard({ addOnKey, question, name }: IisAddOnCardProps) {
  const environment = useInfrastructureWizard((s) => s.environment)
  const region = useInfrastructureWizard((s) => s.region)
  const addOn = useInfrastructureWizard((s) => s[addOnKey])
  const setEnabled = useInfrastructureWizard((s) => s.setIisEnabled)
  const select = useInfrastructureWizard((s) => s.selectIisServer)
  const keepServers = useInfrastructureWizard((s) => s.setExistingIisServers)
  const servers = useExistingIisServers(environment?.id ?? '', region?.id ?? '', addOn.enabled)
  const list = servers.data ?? []

  // The validators read the servers that exist from the store, so what was fetched is kept there.
  useEffect(() => {
    if (servers.data) keepServers(servers.data)
  }, [servers.data, keepServers])

  // Exactly one server: it is the one, so it is chosen for the user.
  useEffect(() => {
    if (addOn.enabled && !addOn.server && servers.data?.length === 1) select(addOnKey, servers.data[0])
  }, [addOn.enabled, addOn.server, servers.data, select, addOnKey])

  return (
    <section className={styles.section} aria-label={name}>
      <YesNoQuestion question={question} name={`addon-${addOnKey}`} value={addOn.enabled} onChange={(answer) => setEnabled(addOnKey, answer)} />
      {addOn.enabled && (
        <>
          {list.length === 1 ? null : (
            <LookupSelect
              id={`addon-${addOnKey}-iis`}
              label={`${name}: IIS server`}
              required
              value={addOn.server?.id ?? ''}
              options={list.map((server) => ({ value: server.id, label: server.name }))}
              onChange={(id) => select(addOnKey, list.find((server) => server.id === id) ?? null)}
              isLoading={servers.isLoading}
              isError={servers.isError}
              errorMessage={lookupErrorMessage(servers.error)}
              onRetry={() => void servers.refetch()}
              emptyMessage={`${NO_IIS_SERVER_MESSAGE}.`}
              hint="Existing IIS servers only."
            />
          )}
          {addOn.server && <ReadOnlyInfoCard title={`${name}: IIS server`} items={details(addOn.server)} note="This server already exists. It is not changed." />}
        </>
      )}
    </section>
  )
}
